import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { PageLoader, Spinner } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import { Dumbbell, Cpu, ShoppingCart, CircuitBoard, Coins, Lock, TrendingUp } from "lucide-react";

const STAT_LABEL = { attack: "Attack", defense: "Defense", speed: "Speed" };

export default function Training() {
  const { pet, refreshPet, patchUser } = useOutletContext();
  const [d, setD] = useState(null);
  const [stat, setStat] = useState("attack");
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/training/status"); setD(data); };
  useEffect(() => { load(); }, []);
  if (!d) return <PageLoader label="Booting the sim deck" />;

  const cap = d.cap;
  const train = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/training/train", { stat });
      playSfx("power"); toast.success(`+1 ${STAT_LABEL[stat]} → ${data.value}/${cap}`);
      patchUser({ credits: data.bits, bits: data.bits });
      await load(); await refreshPet();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const attrsDetail = d.attrs_detail || [];
  const tier = d.tier;
  const cur = attrsDetail.find((a) => a.stat === stat);
  const canTrain = pet && cur && !cur.at_cap && tier.owned > 0 && d.bits >= tier.bits;

  return (
    <div data-testid="training-page">
      <span className="label-cap text-primary flex items-center gap-2"><Dumbbell className="w-4 h-4" /> Training Bay // Drill Sergeant</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-1 font-head">Training Bay</h1>
      <p className="text-muted-foreground mb-6">Spend a Circuit Card + Bits to raise <b className="text-foreground">Attack, Defense or Speed</b> by 1. Each stat is capped at <b className="text-primary">2× your Clearance Level</b> (currently Lv-{d.level} → cap {cap}), and training tiers run to level 50.</p>

      {!pet ? (
        <div className="panel p-8 text-center text-muted-foreground">No active robot. <Link to="/app/create" className="text-primary underline">Fabricate one</Link> first.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="panel-glow p-6 hud-corner text-center" data-testid="training-pod">
            <div className="mx-auto w-fit"><LayeredStack layers={pet.layers} core={pet.core_color} rig={pet.rig} cpu={pet.cpu} size={170} state="idle" /></div>
            <div className="font-head font-bold text-lg mt-2">{pet.name}</div>
            <p className="label-cap flex items-center justify-center gap-1"><TrendingUp className="w-3 h-3" /> Clearance Lv-{d.level} · stat cap {cap}</p>
            <div className="grid grid-cols-3 gap-2 mt-4 text-left">
              {attrsDetail.map((a) => (
                <div key={a.stat} data-testid={`stat-${a.stat}`} className="panel p-2">
                  <div className="label-cap text-[9px]">{STAT_LABEL[a.stat]}</div>
                  <div className={`font-head font-bold mono ${a.at_cap ? "text-accent" : "text-primary"}`}>{a.value}<span className="text-muted-foreground text-xs">/{cap}</span></div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Raise your Clearance Level in the Quest Terminal to unlock higher stat caps.</p>
          </div>

          <div>
            <div className="panel p-5 mb-4">
              <div className="label-cap flex items-center gap-1.5 mb-3"><Cpu className="w-3.5 h-3.5" /> Attribute to Train</div>
              <div className="grid grid-cols-3 gap-2">
                {d.attrs.map((s) => {
                  const a = attrsDetail.find((x) => x.stat === s);
                  return (
                    <button key={s} data-testid={`train-stat-${s}`} onClick={() => setStat(s)}
                      className={`px-2 py-2 rounded-[var(--radius)] text-xs font-bold uppercase mono relative ${stat === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      {STAT_LABEL[s]}{a?.at_cap && <Lock className="w-3 h-3 absolute top-1 right-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="panel p-5" data-testid="train-tier">
              <div className="label-cap flex items-center gap-1.5 mb-3"><CircuitBoard className="w-3.5 h-3.5" /> {tier.label}</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Card required</span><span className={tier.owned > 0 ? "text-primary font-bold" : "text-accent font-bold"}>{tier.card_name} (own {tier.owned})</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Bit cost / point</span><span className="text-secondary font-bold flex items-center gap-1"><Coins className="w-3.5 h-3.5" />{tier.bits}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Your Bits</span><span className="mono">{d.bits}</span></div>
              </div>
              {cur?.at_cap && <p className="text-xs text-accent mt-3">{STAT_LABEL[stat]} is maxed for Lv-{d.level}. Raise your Clearance Level to train higher.</p>}
              <button onClick={train} data-testid="training-do-btn" disabled={busy || !canTrain} className="btn-primary w-full mt-4 py-3">
                {busy ? <Spinner className="w-4 h-4" /> : <><Dumbbell className="w-4 h-4" /> Train +1 {STAT_LABEL[stat]}</>}
              </button>
              <Link to="/app/shop" className="btn-ghost w-full mt-2 text-xs"><ShoppingCart className="w-3.5 h-3.5" /> Buy Circuit Cards</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
